import {useContext, useEffect, useMemo, useState} from "react";
import {GlobalContext} from "../../GlobalReducer";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {ProductInterface} from "../../interfaces/catalog/ProductInterface";
import {FilterInterface} from "../../interfaces/catalog/FilterInterface";
import {ChatListedInterface} from "../../interfaces/customer/ChatInterface";
import {getProducts} from "../../api/catalog";
import ChatProductSlider, {CardRef} from "./ChatProductSlider";
import ChatProductFilters from "./ChatProductFilters";
import {activeFilterCount, brandsOf, EMPTY_FILTER, filterGroups, filterProducts, inStockFirst, priceRangeOf} from "./chatProductFilter";

// Столько карточек добавляет «Показать ещё» — столько же бот ставит в список ответа
const PAGE = 15;

function goods(count: number): string {
    const mod10 = count % 10, mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return 'товар';
    return mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'товара' : 'товаров';
}

function card(product: ProductInterface): CardRef {
    return {id: product.id, href: `/catalog/item/${product.catalog_code}/${product.id}/${product.human_url}`, label: product.name};
}

/**
 * Список товаров ответа консультанта (поиск, похожие) целиком: в тексте ответа — первые 15, сервер прислал все id.
 * Товары грузятся одним запросом /api/list-by-params (цены клиента, наличие, свойства и фильтры) — дальше
 * «Показать ещё» и отбор работают на странице, без вопросов к модели. Сначала товары в наличии, потом остальные.
 * Пока товары грузятся — карточки из текста.
 */
export default function ChatProductBrowser({listed, cards, pageSize = PAGE, filtersOpen = false}: {listed: ChatListedInterface, cards: CardRef[], pageSize?: number, filtersOpen?: boolean}) {
    const [state] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const [products, setProducts] = useState<ProductInterface[] | null>(null);
    const [filters, setFilters] = useState<FilterInterface[]>([]);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState(EMPTY_FILTER);
    const [visible, setVisible] = useState(pageSize);
    const [focus, setFocus] = useState(0);
    const [showFilters, setShowFilters] = useState(filtersOpen);
    const key = listed.ids.join(',');

    useEffect(() => {
        if (!state.customer) return;
        let cancelled = false;
        const order = new Map(listed.ids.map((id, index) => [id, index]));
        getProducts({id: listed.ids.map(String)}, state.customer).then(response => {
            if (cancelled) return;
            // Сначала в наличии, внутри — как прислал сервер (по названию, у похожих — по сходству); каталог отдаёт по-своему
            setProducts(inStockFirst((response.products || [])
                .filter(product => order.has(product.id))
                .sort((a, b) => (order.get(a.id) as number) - (order.get(b.id) as number)), product => product));
            setFilters(response.filters || []);
        }).catch(() => {
            if (!cancelled) setFailed(true);
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, state.customer?.id]);

    const filtered = useMemo(() => products ? filterProducts(products, filter) : [], [products, filter]);
    const groups = useMemo(() => products ? filterGroups(products, filters) : [], [products, filters]);
    const brands = useMemo(() => products ? brandsOf(products) : [], [products]);
    const priceRange = useMemo(() => priceRangeOf(products || []), [products]);
    const byId = useMemo(() => {
        const map: Record<number, ProductInterface> = {};
        (products || []).forEach(product => map[product.id] = product);
        return map;
    }, [products]);

    const changeFilter = (next: typeof filter) => {
        setFilter(next);
        setVisible(pageSize);
        setFocus(0);
    };

    // Каталог недоступен — как раньше: карточки из текста (ссылками, если данных нет)
    if (!products) {
        return <ChatProductSlider cards={cards} products={{}} loading={!failed}/>;
    }

    const rest = filtered.length - visible;
    const more = Math.min(pageSize, rest);
    const active = activeFilterCount(filter);

    return <div className="chat-browser">
        {filtered.length > 0
            ? <ChatProductSlider cards={filtered.slice(0, visible).map(card)} products={byId} loading={false} focus={focus}/>
            : <div className="chat-browser__empty">Под этот отбор товаров нет — ослабьте условия.</div>}
        <div className="chat-browser__bar">
            <span className="chat-browser__count">
                Показано {Math.min(visible, filtered.length)} из {filtered.length}
                {active > 0 && ` (всего в списке ${products.length})`}
                {listed.total > listed.ids.length && ` — найдено ${listed.total} ${goods(listed.total)}, здесь первые ${listed.ids.length}`}
            </span>
            {more > 0 && <button type="button" className="btn btn-primary chat-browser__more" onClick={() => {
                setFocus(visible);
                setVisible(visible + pageSize);
            }}>Показать ещё {more}</button>}
            {(groups.length > 0 || brands.length > 1 || products.length > 1) && (
                <button type="button" className={'btn gray chat-browser__filters' + (active ? ' chat-browser__filters--active' : '')}
                        onClick={() => setShowFilters(!showFilters)}>
                    Фильтры{active ? ` (${active})` : ''}
                </button>
            )}
        </div>
        {showFilters && <ChatProductFilters filter={filter} setFilter={changeFilter} groups={groups} brands={brands} priceRange={priceRange}
                                            withStock={products.some(product => product.client_price)}/>}
    </div>;
}
